/**
 * Media Upload Hook
 * 
 * Handles uploads of photos, videos, and voice messages to R2 storage.
 * Supports optimistic UI, progress tracking, and compression.
 * Also supports YouTube/Vimeo links for videos (no upload needed).
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { R2StorageService, type R2UploadResult } from '@/services/storage/r2-storage.service';
import { isVideoUrl, parseAndValidateVideoUrl } from '@/utils/video-url';

export interface MediaUploadOptions {
  compress?: boolean;
  generateThumbnail?: boolean;
  maxSize?: number;
  folder?: string;
}

export function useMediaUpload() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const r2Service = R2StorageService.getInstance();

  const uploadPhoto = useCallback(async (
    file: File,
    options: MediaUploadOptions = {}
  ): Promise<string | null> => {
    if (!user) {
      toast.error('You must be logged in to upload photos');
      return null;
    }

    const maxSize = options.maxSize || 10 * 1024 * 1024; // 10MB default for photos
    if (file.size > maxSize) {
      toast.error(`Photo must be smaller than ${maxSize / (1024 * 1024)}MB`);
      return null;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await r2Service.uploadFile(file, {
        folder: options.folder || `media/photos/${user.id}`,
        compress: options.compress !== false, // Default to true
        generateThumbnail: options.generateThumbnail !== false,
        maxSize,
        onProgress: (progress) => setUploadProgress(progress),
      });

      setUploadProgress(100);
      return result.url;
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      toast.error(error.message || 'Failed to upload photo');
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [user, r2Service]);

  const uploadVideo = useCallback(async (
    fileOrUrl: File | string,
    options: MediaUploadOptions = {}
  ): Promise<{ url: string; thumbnailUrl?: string; provider?: 'youtube' | 'vimeo' } | null> => {
    if (!user) {
      toast.error('You must be logged in to upload videos');
      return null;
    }

    // Check if input is a YouTube/Vimeo URL
    if (typeof fileOrUrl === 'string') {
      if (!isVideoUrl(fileOrUrl)) {
        toast.error('Invalid video URL. Please provide a valid YouTube or Vimeo link.');
        return null;
      }

      const parsed = parseAndValidateVideoUrl(fileOrUrl);
      if (!parsed) {
        toast.error('Could not parse video URL');
        return null;
      }

      // Return URL details without uploading
      return {
        url: parsed.url,
        thumbnailUrl: parsed.thumbnailUrl,
        provider: parsed.provider,
      };
    }

    // Handle file upload
    const file = fileOrUrl;
    const maxSize = options.maxSize || 100 * 1024 * 1024; // 100MB default for videos
    if (file.size > maxSize) {
      toast.error(`Video must be smaller than ${maxSize / (1024 * 1024)}MB`);
      return null;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await r2Service.uploadFile(file, {
        folder: options.folder || `media/videos/${user.id}`,
        compress: false, // Videos are compressed on the server
        generateThumbnail: true, // Always generate thumbnail for videos
        maxSize,
        onProgress: (progress) => setUploadProgress(progress),
      });

      setUploadProgress(100);
      return {
        url: result.url,
        thumbnailUrl: result.thumbnailUrl,
      };
    } catch (error: any) {
      console.error('Error uploading video:', error);
      toast.error(error.message || 'Failed to upload video');
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [user, r2Service]);

  const uploadVoice = useCallback(async (
    file: File,
    options: MediaUploadOptions = {}
  ): Promise<string | null> => {
    if (!user) {
      toast.error('You must be logged in to upload voice messages');
      return null;
    }

    const maxSize = options.maxSize || 10 * 1024 * 1024; // 10MB default for voice
    if (file.size > maxSize) {
      toast.error(`Voice message must be smaller than ${maxSize / (1024 * 1024)}MB`);
      return null;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await r2Service.uploadFile(file, {
        folder: options.folder || `media/voice/${user.id}`,
        compress: false, // Voice is already compressed (Opus)
        generateThumbnail: false,
        maxSize,
        onProgress: (progress) => setUploadProgress(progress),
      });

      setUploadProgress(100);
      return result.url;
    } catch (error: any) {
      console.error('Error uploading voice message:', error);
      toast.error(error.message || 'Failed to upload voice message');
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [user, r2Service]);

  const uploadMultiplePhotos = useCallback(async (
    files: File[],
    options: MediaUploadOptions = {}
  ): Promise<string[]> => {
    const uploadPromises = files.map(file => uploadPhoto(file, options));
    const results = await Promise.all(uploadPromises);
    return results.filter((url): url is string => url !== null);
  }, [uploadPhoto]);

  /**
   * Handle video input - accepts either File or URL string
   * Automatically detects YouTube/Vimeo URLs and handles accordingly
   */
  const handleVideo = useCallback(async (
    input: File | string,
    options: MediaUploadOptions = {}
  ): Promise<{ url: string; thumbnailUrl?: string; provider?: 'youtube' | 'vimeo' } | null> => {
    return uploadVideo(input, options);
  }, [uploadVideo]);

  return {
    uploadPhoto,
    uploadVideo,
    uploadVoice,
    uploadMultiplePhotos,
    handleVideo, // Convenience function for videos (File or URL)
    uploading,
    uploadProgress,
  };
}
